import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { StateStorageService } from 'app/core/auth/state-storage.service';
import { SessionStorageService } from 'ngx-webstorage';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { TwoStepVerificationSmsService } from '../../pages/security/twostepverification/extraUser/two-step-verification-sms-service';
import { TwoFADevice } from '../../pages/security/twostepverification/model/device.model';
import { TwoStepVerificationService } from '../../pages/security/twostepverification/two-step-verification.service';
import { AccountACL } from '../../shared/constants/account-acl.constants';
import {
  retrievedPasswordExpirationConfig,
  retrievedTwoFaInfos,
  userLoggedin,
  retrieved2faClientStatus,
  retrievedWebMailClientUrl,
  retrieveduserInfoPecPlus,
  retrieved2faClientThirdPartsGrants,
  retrievedOtpDisplayAvailable,
  updateUserLanguage,
} from '../../shared/state/user.actions';
import { Account, AccountUpdate } from '../models/account.model';
import { AppConfiguration } from '../models/app-configuration.model';
import { TwoFaClientService } from '../../shared/service/twofa-client.service';
import { TwoFaClient, WebMailInfo } from '../../shared/model/twofa-client.model';
import { ExpiryService } from 'app/pages/security/change/expiry.service';
import { Expiry } from 'app/pages/security/change/expiry.model';
import { UserInfo } from 'app/shared/model/user-info.model';
import { PecPlusService } from 'app/shared/service/pec-plus.service';
import { SubAccountService } from 'app/shared/service/sub-account.service';
import { SubAccount } from 'app/shared/model/sub-account';
import { OtpDisplayService } from 'app/shared/service/otp-display.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Angular Service, implements method for account management
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  constructor(
    private readonly languageService: TranslateService,
    private readonly sessionStorage: SessionStorageService,
    private readonly http: HttpClient,
    private readonly stateStorageService: StateStorageService,
    private readonly router: Router,
    private readonly appConfig: AppConfiguration,
    private readonly store: Store,
    private readonly expiryConfigService: ExpiryService,
    private readonly twoStepVerificationSrv: TwoStepVerificationService,
    private readonly twoSvSmsService: TwoStepVerificationSmsService,
    private readonly twoFaClientService: TwoFaClientService,
    private readonly pecPlusService: PecPlusService,
    private readonly subAccountService: SubAccountService,
    private readonly otpDisplayService: OtpDisplayService
  ) { }

  /**
   * Update account information
   * @param account
   */
  save(account: AccountUpdate): Observable<{}> {
    return this.http.put(`${this.appConfig.serverApiUrl}/api/v1/my-account`, account);
  }

  /**
   * Try to fetch account information of current logged user
   * if success set current user language
   */
  identity(firstLogin: boolean): Observable<Account | null> {
    return this.fetch(firstLogin).pipe(
      catchError(() => of(null)),
      tap((account: Account | null) => {

        if (!account) {
          this.store.dispatch(userLoggedin({ user: account }));
          return;
        }
        account.name = account.name || '';
        account.lastname = account.lastname || '';

        // After retrieve the account info, the language will be changed to
        // the user's preferred language configured in the account setting
        if (account.lang) {
          /*** Inizio controllo docaposte */
          if (account.acls.some((authority: AccountACL) => [AccountACL.DOCAPOSTE_VIEW].includes(authority))) {
            const langKeyDP = "fr";
            this.sessionStorage.store('locale', langKeyDP);
            this.languageService.use(langKeyDP);
            this.store.dispatch(updateUserLanguage({ lang: langKeyDP }));

            const accountUpdate = new AccountUpdate(account.name, account.lastname, langKeyDP);
            this.save(accountUpdate).subscribe();
          } else {
            /*** fine docaposte */
            const langKey = this.sessionStorage.retrieve('locale') || account.lang;

            this.languageService.use(langKey);
          }
        }
        this.store.dispatch(userLoggedin({ user: account }));


        // Load current password expiration config only if account has correct ACL
        if (account.acls.some((authority: AccountACL) => [AccountACL.PASSWORD_EXPIRATION_CONFIG_VIEW].includes(authority))) {
          this.loadExpiryConfig();
        }
        this.loadUserInfoPecPlus();
        this.loadTwoFaCurrentStatus(account);

        this.navigateToStoredUrl();

        if (account.userType === 'pec-multiutente') {
          // only for pec-multiutente
          /* recuperare le grant per client third parts */
          this.subAccountService.getSubAccountInfo().subscribe((res: SubAccount) => {
            if (res) {
              this.store.dispatch(retrieved2faClientThirdPartsGrants({ clientThirdPartsGrants: res }));
            }
          });
        }

        // recuperare is available otp display
        this.otpDisplayService
          .getOtpDisplayAvailable()
          .pipe(
            catchError(() => of({ data: false }))
          )
          .subscribe((result: { data: boolean }) => {
            this.store.dispatch(retrievedOtpDisplayAvailable({ otpDisplayAvailable: result.data }));
          });

      })
    );
  }

  /**
   * Load password expiration config
   * If error, return null. If success, save in store
   */
  public loadExpiryConfig(): void {
    this.expiryConfigService
      .getExpirationConfig()
      .pipe(
        catchError(() => of(null))
      )
      .subscribe((expiry: Expiry | null) => {
        this.store.dispatch(retrievedPasswordExpirationConfig({ expiryConfig: expiry }));
      });
  }

  /**
   * Load user info pec plus.
   * If error, do nothing. If success, save in store.
   */
  public loadUserInfoPecPlus(): void {
    this.pecPlusService.getUserInfo().subscribe((userInfo: UserInfo | null) => {
      if (!userInfo) {
        return;
      }
      this.store.dispatch(retrieveduserInfoPecPlus({ userInfoPecPlus: userInfo }));
    });
  }

  /**
   * If account access control list includes 2A app view or user type is pec-multiutente, get associated device.
   * If success and account ACL list does not include 2FA email view, set device to store.
   * Otherwise get sms 2FA status and set 2FA infos to store if success.
   * @param {Account} account
   */
  public loadTwoFaCurrentStatus(account: Account): void {
    if (
      account.acls.some((authority: AccountACL) => [AccountACL.TWOFA_APP_VIEW].includes(authority)) ||
      (account.userType !== null && account.userType !== '' && account.userType.toLowerCase() === 'pec-multiutente')
    ) {
      this.twoStepVerificationSrv
        .getAssociatedDevice() // verifica device info
        .pipe(
          catchError(() => of([] as TwoFADevice[])),
          map((res: TwoFADevice[]) => (res && res.length > 0 ? res[0] : undefined))
        )
        .subscribe((device: TwoFADevice | undefined) => {

          if (!account.acls.some((authority: AccountACL) => [AccountACL.TWOFA_EMAIL_VIEW].includes(authority))) {
            this.store.dispatch(retrievedTwoFaInfos({ status: device ? true : false, device }));
          }
        });
    } else {
      this.twoSvSmsService
        .get2FaStatus()
        .pipe(
          catchError(() => of({ enabled: false }))
        )
        .subscribe((currentStatus: { enabled: boolean }) => {
          this.store.dispatch(retrievedTwoFaInfos({ status: currentStatus.enabled }));
        });
    }

    this.loadTwoFaClientInfos();
  }

  /**
   * Load 2FA client information.
   * - Set 2FA client status to store.
   * - Get logged company.
   * - If company is not pec, get link for protocol activation ad set web mail client url to store.
   */
  public loadTwoFaClientInfos(): void {
    this.twoFaClientService
      .getCurrent2faClientStatus()
      .pipe(
        catchError((resError: HttpErrorResponse) => {
          if (resError.status !== 0
            &&  resError.error.message === 'error.clientApi.disabledProtocolsUpdate') {
              return of({ hasValidApiKey: false, hasActiveProtocol: false, hasProtocolServiceUpdate: false } as TwoFaClient);
          }
          return of({ hasValidApiKey: false, hasActiveProtocol: false, hasProtocolServiceUpdate: true } as TwoFaClient);
        })
      )
      .subscribe((currentClientStatus: TwoFaClient) => {
        if(currentClientStatus.hasProtocolServiceUpdate === undefined) {
          currentClientStatus.hasProtocolServiceUpdate=true;
        }
        this.store.dispatch(retrieved2faClientStatus({ twoFaClientStatus: currentClientStatus }));
      });

    const currentCompany = this.stateStorageService.getLoggedCompany();

    /**
     * Static control, just to reduce the call load to api gateway
     */
    if (currentCompany === 'pec') {
      this.store.dispatch(retrievedWebMailClientUrl({ webMailClientUrl: this.appConfig.webmailUrl }));
      return;
    }

    this.twoFaClientService
      .getActivateProtocolLink(currentCompany)
      .pipe(
        catchError(() => of(new WebMailInfo('', '')))
      )
      .subscribe((webMailInfo: WebMailInfo) => {
        this.store.dispatch(retrievedWebMailClientUrl({ webMailClientUrl: webMailInfo.redirectWebMail }));
      });
  }

  /**
  * Fetch account info
  */
  public fetch(noCache: boolean): Observable<Account> {
    const params = new HttpParams().set('noCache', noCache);
    return this.http.get<Account>(`${this.appConfig.serverApiUrl}/api/v1/my-account`, { params });
  }

  /**
   * Check if there is a stored url and if is present redirect user to it
   */
  private navigateToStoredUrl(): void {
    // previousState can be set in the authExpiredInterceptor
    // if login is successful, go to stored previousState and clear previousState
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    }
  }
}
